import React from 'react';
import {
  Avatar,
  Button,
  Chip,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import BedtimeRoundedIcon from '@mui/icons-material/BedtimeRounded';
import moment from 'moment';
import { InboxConversation } from 'data/api/conversation.api';
import {
  SnoozeButton,
  SnoozeFailure,
} from 'components/conversation/SnoozeButton';
import { calendarTime } from 'components/conversation/snooze';

const initialsOf = (name?: string) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?';

// Intercom-style rows carry a plain-text preview; the parts come back as HTML.
const toPreview = (value?: string) => {
  if (!value) return 'No messages yet';
  const text = value
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text || 'No messages yet';
};

// Tonal buttons, per Sam's mock: Snooze neutral, Close warm, Reopen teal.
const action = (
  backgroundColor: string,
  color: string,
  hover: string,
): SxProps<Theme> => ({
  backgroundColor,
  color,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '13px',
  lineHeight: '20px',
  minWidth: 'unset',
  padding: '2px 12px',
  borderRadius: '8px',
  ':hover': { backgroundColor: hover },
});
const SNOOZE_SX = action('#F1EEEA', '#54596A', '#E7E2DC');
const CLOSE_SX = action('#F8E9E4', '#8A3B2B', '#F1DAD2');
const REOPEN_SX = action('#E8F0EF', '#356766', '#D8E6E4');

interface IQueueRowProps {
  conversation: InboxConversation;
  selected: boolean;
  busy: boolean;
  onSelect: (conversation: InboxConversation) => void;
  onToggleStatus: (conversation: InboxConversation) => void;
  onSnooze: (
    conversation: InboxConversation,
    until: string,
  ) => Promise<SnoozeFailure | null>;
}

export const QueueRow: React.FC<IQueueRowProps> = ({
  conversation,
  selected,
  busy,
  onSelect,
  onToggleStatus,
  onSnooze,
}) => {
  const isOpen = conversation.inboxStatus === 'open';
  const snoozedUntil =
    conversation.inboxStatus === 'snoozed' && conversation.snoozedUntil
      ? calendarTime(moment(conversation.snoozedUntil), moment())
      : undefined;
  // segmentCount is Intercom plumbing — a rep sees one conversation, so it is
  // deliberately not rendered.
  const awaitingReply = conversation.lastMessageAuthorType === 'user';

  return (
    <Stack
      onClick={() => onSelect(conversation)}
      sx={{
        cursor: 'pointer',
        padding: '12px 14px',
        gap: '6px',
        borderBottom: '1px solid #EFEDEB',
        borderLeft: selected ? '3px solid #213B54' : '3px solid transparent',
        backgroundColor: selected ? '#F4F6F8' : 'transparent',
        ':hover': {
          backgroundColor: selected ? '#F4F6F8' : '#FAF9F8',
          '& .queue-row-actions': { visibility: 'visible' },
        },
      }}
    >
      <Stack direction="row" gap={1.25} alignItems="flex-start">
        <Avatar
          sx={{
            width: 34,
            height: 34,
            fontSize: '13px',
            fontWeight: 700,
            color: '#FFFFFF',
            backgroundColor: '#213B54',
          }}
        >
          {initialsOf(conversation.memberName)}
        </Avatar>
        <Stack sx={{ minWidth: 0, flex: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
          >
            <Typography
              noWrap
              sx={{ fontSize: '15px', fontWeight: 600, color: '#213B54' }}
            >
              {conversation.memberName}
            </Typography>
            <Typography
              sx={{ fontSize: '12px', color: '#7D818C', whiteSpace: 'nowrap' }}
            >
              {conversation.lastMessageAt
                ? moment.unix(conversation.lastMessageAt).fromNow(true)
                : '—'}
            </Typography>
          </Stack>
          <Typography noWrap sx={{ fontSize: '13px', color: '#7D818C' }}>
            {conversation.companyName || 'No agency'}
          </Typography>
          <Typography
            sx={{
              fontSize: '13px',
              color: '#54596A',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {toPreview(conversation.lastMessagePreview)}
          </Typography>
        </Stack>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        gap={0.75}
        sx={{ flexWrap: 'wrap' }}
      >
        {!!snoozedUntil && (
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ color: '#5C534A' }}
          >
            <BedtimeRoundedIcon sx={{ fontSize: '15px' }} />
            <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
              Snoozed until {snoozedUntil}
            </Typography>
          </Stack>
        )}
        {awaitingReply && (
          <Chip
            label="Awaiting reply"
            size="small"
            sx={{
              backgroundColor: '#FFF4E5',
              color: '#8A5300',
              fontWeight: 600,
              height: '22px',
            }}
          />
        )}
        {!!conversation.escalatedAt && (
          <Tooltip
            title={[conversation.escalatedByName, conversation.escalationReason]
              .filter(Boolean)
              .join(' — ')}
          >
            <Chip
              label="Escalated"
              size="small"
              sx={{
                backgroundColor: '#FEF3F2',
                color: '#B42318',
                fontWeight: 600,
                height: '22px',
              }}
            />
          </Tooltip>
        )}
        {!!conversation.assignedToName && (
          <Typography sx={{ fontSize: '12px', color: '#7D818C' }}>
            {conversation.assignedToName}
          </Typography>
        )}
        <Stack
          className="queue-row-actions"
          direction="row"
          gap={0.75}
          sx={{
            marginLeft: 'auto',
            visibility: selected ? 'visible' : 'hidden',
          }}
        >
          {isOpen && (
            <SnoozeButton
              disabled={busy}
              sx={SNOOZE_SX}
              onSnooze={(until) => onSnooze(conversation, until)}
            />
          )}
          <Button
            size="small"
            disabled={busy}
            onClick={(event) => {
              event.stopPropagation();
              onToggleStatus(conversation);
            }}
            sx={isOpen ? CLOSE_SX : REOPEN_SX}
          >
            {isOpen ? 'Close' : 'Reopen'}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
